import { Combobox } from "@headlessui/react";
import { DocumentTextIcon, ChevronUpDownIcon, KeyIcon, PencilIcon, EyeIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import { useLanguage } from "contexts/LanguageContext";
import { useStorage } from "hooks/useStorage";
import { useTemplates } from "hooks/useTemplates";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { Template } from "types/template";
import { DocGenerator } from "utils/docGenerator";
import { extractTemplateFields } from "utils/extractDocFields";
import { isQuotation } from "./DocumentGeneratorModal";
import SelectedDocument from "./SelectedDocument";
import ChooseTemplatePage from "./steps/ChooseTemplatePage";
import EditDataPage from "./steps/EditDataPage";
import GeneratePage from "./steps/GeneratePage";

interface RenderGenerationStepsProps {
  documentDataProvider: Quotation | Customer | null;
  documentData: Record<string, string>;
  setDocumentData: (data: Record<string, string>) => void;
  isOpen: boolean;
  onClose: () => void;
  handleGenerateDocument: () => Promise<{url: string, fileName: string} | null | undefined>;
  selectedTemplate: Template | null;
  setSelectedTemplate: (template: Template | null) => void;
  selectedFormats: string[];
  setSelectedFormats: (formats: string[]) => void;
  isGenerating: boolean;
  forceContractTemplates: boolean;
  generatedFiles?: {url: string, fileName: string}[];
}

const RenderGenerationSteps = ({
  documentDataProvider,
  documentData = {},
  setDocumentData = () => { },
  isOpen = false,
  onClose = () => { },
  handleGenerateDocument = async () => { return null; },
  selectedTemplate = null,
  setSelectedTemplate = () => { },
  selectedFormats = ['docx'],
  setSelectedFormats = () => { },
  isGenerating = false,
  forceContractTemplates = false,
  generatedFiles = [],
}: RenderGenerationStepsProps) => {
  const [templateFields, setTemplateFields] = useState<string[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingValue, setEditingValue] = useState<string>('');
  const [step, setStep] = useState<'template' | 'edit' | 'generate'>('template');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [generatedFileUrl, setGeneratedFileUrl] = useState<string | null>(null);
  const [generatedFileName, setGeneratedFileName] = useState<string | null>(null);

  const { uploadFile } = useStorage();
  const { t } = useLanguage();

  const { loading, error, refreshTemplates, getTemplateByType } = useTemplates();
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!documentDataProvider) return;
      const templates = await getTemplateByType(isQuotation(documentDataProvider) ? 'quotation' : 'contract');
      setTemplates(templates);
    }
    fetchTemplates();
  }, [documentDataProvider, getTemplateByType]);

  // Mező-érték párok rendezett listája és számláló a kitöltött értékekről
  const { sortedDocumentData, filledCount, filledPercentage } = useMemo(() => {
    if (!documentDataProvider || templateFields.length === 0) return { sortedDocumentData: [], filledCount: 0, filledPercentage: 0 };

    const filled = templateFields.filter(key => {
      const value = documentData[key];
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== null && value !== undefined;
    }).length;

    const percentage = templateFields.length > 0 ? Math.round((filled / templateFields.length) * 100) : 0;

    return {
      sortedDocumentData: templateFields.map(key => [key, documentData[key] || '']),
      filledCount: filled,
      filledPercentage: percentage
    };
  }, [documentDataProvider, documentData, templateFields]);

  // Rendezett sablonok a kereső alapján
  const filteredTemplates = useMemo(() => {
    return templates.filter(template =>
      template.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(templateSearchQuery.toLowerCase())
    );
  }, [templates, templateSearchQuery]);

  useEffect(() => {
    if (isOpen) {
      refreshTemplates();
    }
  }, [isOpen, refreshTemplates]);

  useEffect(() => {
    if (!isOpen) {
      // Reset all states when modal is closed
      setTemplateFields([]);
      setEditingKey('');
      setEditingValue('');
      setStep('template');
      setShowPreview(false);
      setPreviewUrl(null);
      setTemplateSearchQuery('');
      setSelectedTemplate(null);
      setGeneratedFileUrl(null);
      setGeneratedFileName(null);
    }
  }, [isOpen]);

  const handleGenerateAndClose = async () => {
    if (handleGenerateDocument) {
      const result = await handleGenerateDocument();
      
      if (result) {
        setGeneratedFileUrl(result.url);
        setGeneratedFileName(result.fileName);
      }
    }
  };

  const handleTemplateSelect = async (template: Template) => {
    setSelectedTemplate(template);
    // Ha kiválasztottunk egy sablont, elemezzük a tartalmát
    if (template && template.fileUrl) {
      const fields = await extractTemplateFields(template);
      setTemplateFields(fields);
    }
  };

  // Érték szerkesztés megkezdése
  const handleEdit = (key: string, value: string) => {
    setEditingKey(key);
    setEditingValue(value);
  };

  // Érték mentése
  const handleSaveEdit = () => {
    if (editingKey) {
      setDocumentData({
        ...documentData,
        [editingKey]: editingValue
      });
      setEditingKey('');
      setEditingValue('');
    }
  };

  // Érték szerkesztés megszakítása
  const handleCancelEdit = () => {
    setEditingKey('');
    setEditingValue('');
  };

  // Következő lépés
  const handleNextStep = () => {
    if (step === 'template') {
      setStep('edit');
    } else if (step === 'edit') {
      setStep('generate');
    }
  };

  // Előző lépés
  const handlePrevStep = () => {
    if (step === 'edit') {
      setStep('template');
    } else if (step === 'generate') {
      setStep('edit');
    }
  };

  // Előnézet bezárása
  const closePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
  };

  // Előnézet generálása a behelyettesített értékekkel
  const generatePreview = async () => {
    if (!selectedTemplate || !selectedTemplate.fileUrl) return;

    setIsGeneratingPreview(true);
    setIsLoadingPreview(true);

    try {
      // Generálunk egy dokumentumot a behelyettesített értékekkel
      const dataToUse = [documentData];

      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        dataToUse,
        ['docx']
      );

      if (generatedDocs['docx'] && generatedDocs['docx'].length > 0) {
        const generatedDocumentBlob = generatedDocs['docx'][0];
        // Ideiglenes fájlnév generálása
        const tempFileName = `preview_${Date.now()}.docx`;

        // Feltöltjük a generált dokumentumot
        const fileUrl = await uploadFile(
          new File([generatedDocumentBlob], tempFileName, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
          'previews'
        );

        if (fileUrl) {
          // Közvetlenül a Firebase Storage URL-t használjuk
          setPreviewUrl(fileUrl);
          setShowPreview(true);
        } else {
          throw new Error("Nem sikerült feltölteni az előnézeti dokumentumot");
        }
      } else {
        throw new Error("Nem sikerült generálni az előnézeti dokumentumot");
      }
    } catch (error) {
      console.error("Hiba az előnézet generálása közben:", error);
      toast.error("Nem sikerült létrehozni az előnézetet");
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  if (!documentDataProvider) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <DocumentTextIcon className="h-12 w-12 text-gray-500 mb-4" />
        <p className="text-gray-400 text-sm text-center">
          Válassz egy dokumentumot a generáláshoz
        </p>
      </div>
    );
  }

  return (
    <>
      {(step === 'template') && (
        <ChooseTemplatePage
          documentDataProvider={documentDataProvider}
          templates={templates}
          selectedTemplate={selectedTemplate}
          loading={loading}
          handleTemplateSelect={handleTemplateSelect}
          templateSearchQuery={templateSearchQuery}
          setTemplateSearchQuery={setTemplateSearchQuery}
          templateFields={templateFields}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
          forceContractTemplates={forceContractTemplates}
        />
      )}
      {(step === 'edit') && (
        <EditDataPage
          documentDataProvider={documentDataProvider}
          sortedDocumentData={sortedDocumentData}
          filledPercentage={filledPercentage}
          filledCount={filledCount}
          templateFields={templateFields}
          documentData={documentData}
          editingKey={editingKey}
          editingValue={editingValue}
          setEditingValue={setEditingValue}
          handleSaveEdit={handleSaveEdit}
          handleCancelEdit={handleCancelEdit}
          handleEdit={handleEdit}
          selectedTemplate={selectedTemplate}
          closePreview={closePreview}
        />
      )}
      {(step === 'generate') && (
        <GeneratePage
          selectedTemplate={selectedTemplate}
          selectedFormats={selectedFormats}
          setSelectedFormats={setSelectedFormats}
          isGenerating={isGenerating}
          handleGenerateAndClose={handleGenerateAndClose}
          sortedDocumentData={sortedDocumentData}
          filledPercentage={filledPercentage}
          filledCount={filledCount}
          generatedFileUrl={generatedFileUrl}
          generatedFileName={generatedFileName}
          generatedFiles={generatedFiles}
        />
      )}

      {/* Navigációs gombok - most fixen az oldal alján */}
      <div className="p-4 border-t border-gray-100 bg-white shadow-sm">
        <div className="flex justify-between">
          {step !== 'template' ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              {t('common.back')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              {t('common.cancel')}
            </button>
          )}

          {step === 'template' && (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={!selectedTemplate}
              className={`px-4 py-2 text-xs font-medium rounded-md ${selectedTemplate
                ? 'text-white bg-teal-600 hover:bg-teal-700'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
              }`}
            >
              {t('common.next')}
            </button>
          )}

          {step === 'edit' && (
            <button
              type="button"
              onClick={handleNextStep}
              className="px-4 py-2 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md"
            >
              {t('common.next')}
            </button>
          )}
        </div>
      </div>
    </>
  )
};

export default RenderGenerationSteps;

